import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Brain, ArrowRight, FileText, Target, AlertTriangle, Sparkles, Clock, TrendingUp, Zap, Activity, Download, Loader2 } from 'lucide-react';
import { api, DOMAIN_NAMES, getScoreLevel, BELIEF_ARCHITECTURE_PATTERNS, formatDate } from '../lib/api';
import MindWheel from '../components/visualizations/MindWheel';
import BeliefArchitectureMap from '../components/visualizations/BeliefArchitectureMap';
import NeuralFrictionMap from '../components/visualizations/NeuralFrictionMap';
import MindArchitectureLayers from '../components/visualizations/MindArchitectureLayers';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export default function Report() {
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const response = reportId 
          ? await api.get(`/reports/${reportId}`)
          : await api.get('/reports/latest');
        setReport(response.data);
      } catch (error) {
        console.error('Error fetching report:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [reportId]);

  const handleExportPDF = useCallback(async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const element = reportRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#0a0f0d',
        logging: false,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight
      });
      const imgData = canvas.toDataURL('image/jpeg', 0.85);
      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const pdf = new jsPDF('p', 'mm', 'a4');
      let heightLeft = imgHeight;
      let position = 0;
      const pageHeight = 297; // A4 height in mm

      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = -(imgHeight - heightLeft);
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`MindFlo_Report_${formatDate(report.created_at).replace(/\s/g, '_')}.pdf`);
    } catch (error) {
      console.error('PDF export error:', error);
    } finally {
      setExporting(false);
    }
  }, [report]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground font-mono text-sm">Loading report...</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center">
        <FileText className="h-16 w-16 text-muted-foreground/50 mb-4" strokeWidth={1} />
        <h2 className="text-xl font-semibold mb-2">No Report Available</h2>
        <p className="text-muted-foreground mb-6">Complete an assessment to generate your diagnostic report.</p>
        <Link to="/assessment/full">
          <Button className="glow-primary" data-testid="start-assessment">
            Start Diagnostic
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
    );
  }

  // Support both old and new field names
  const stabilityIndex = report.stability_index || report.mind_index || 50;
  const neuralFriction = report.neural_friction_score ?? report.cognitive_risk_index ?? 0;
  const pattern = report.belief_architecture_pattern || report.cluster_profile;
  const interpretation = report.interpretation;
  
  const stabilityLevel = getScoreLevel(100 - stabilityIndex);
  const patternInfo = BELIEF_ARCHITECTURE_PATTERNS[pattern?.primary_cluster];

  return (
    <div className="space-y-8 max-w-5xl mx-auto" data-testid="report-page" ref={reportRef}>
      {/* Report Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <Card className="bg-card border-border/50">
          <CardContent className="report-card">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Brain className="h-7 w-7 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold tracking-tight">MindFlo Diagnostic Report</h1>
                    <p className="text-sm text-muted-foreground">Generated {formatDate(report.created_at)}</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={handleExportPDF} disabled={exporting}
                  data-testid="export-pdf-btn" className="w-fit">
                  {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                  {exporting ? 'Generating PDF...' : 'Download PDF'}
                </Button>
              </div>
              <div className="flex gap-10">
                <div className="text-center">
                  <p className="report-label">Stability Index</p>
                  <p className="text-5xl font-bold font-mono mt-1" style={{ color: stabilityLevel.color }}>
                    {stabilityIndex.toFixed(1)}
                  </p>
                  <p className="text-xs mt-1" style={{ color: stabilityLevel.color }}>{stabilityLevel.label}</p>
                </div>
                <Separator orientation="vertical" className="h-20" />
                <div className="text-center">
                  <p className="report-label">Neural Friction</p>
                  <p className="text-5xl font-bold font-mono mt-1 text-amber-400">{neuralFriction}</p>
                  <p className="text-xs text-amber-400/70 mt-1">of 7 domains</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* System Layer Map */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
        <Card className="bg-card border-border/50">
          <CardContent className="report-card">
            <h2 className="report-section-title">System Layer Map</h2>
            <p className="report-subheading">Click any layer to jump to its detailed analysis</p>
            <MindArchitectureLayers domainScores={report.domain_scores} navigable={true} />
          </CardContent>
        </Card>
      </motion.div>

      {/* 1. Mind Architecture Summary */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <Card className="bg-card border-border/50" id="cognitive-pattern-explanation">
          <CardContent className="report-card">
            <h2 className="report-section-title flex items-center gap-2">
              <Brain className="h-5 w-5 text-primary" strokeWidth={1.5} />
              Mind Architecture Summary
            </h2>
            <p className="report-subheading">An overview of your cognitive architecture based on the 7-domain analysis</p>
            <div className="p-5 rounded-lg bg-secondary/20 border border-border/40 space-y-3">
              {interpretation?.mind_architecture_summary?.map((point, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="text-primary mt-0.5 text-lg leading-none">&#8226;</span>
                  <span className="report-body">{point}</span>
                </div>
              )) || (
                <p className="report-body">{report.executive_summary}</p>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* 2. Core Belief Pattern */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
        <Card className="bg-card border-border/50">
          <CardContent className="report-card">
            <h2 className="report-section-title flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" strokeWidth={1.5} />
              Core Belief Pattern
            </h2>
            <p className="report-subheading">Your dominant belief architecture and supporting cognitive loops</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <p className="report-label mb-3">Dominant Belief Pattern</p>
                <div className="p-5 rounded-lg border-2" style={{
                  borderColor: patternInfo?.color || '#6366F1',
                  backgroundColor: `${patternInfo?.color || '#6366F1'}08`
                }}>
                  <h3 className="text-xl font-bold" style={{ color: patternInfo?.color }}>
                    {interpretation?.core_belief_patterns?.dominant_pattern || patternInfo?.name || 'Unknown'}
                  </h3>
                  {patternInfo?.description && (
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{patternInfo.description}</p>
                  )}
                </div>
                {(interpretation?.core_belief_patterns?.supporting_patterns || patternInfo?.supporting)?.length > 0 && (
                  <div className="mt-5">
                    <p className="report-label mb-3">Supporting Patterns</p>
                    <div className="space-y-2">
                      {(interpretation?.core_belief_patterns?.supporting_patterns || patternInfo?.supporting)?.map((p, i) => (
                        <div key={i} className="flex items-center gap-2.5 text-sm">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: patternInfo?.color || '#6366F1', opacity: 0.6 }} />
                          <span>{p}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-center">
                <BeliefArchitectureMap beliefPattern={pattern} domainScores={report.domain_scores} />
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* 3. Neural Friction Drivers */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <Card className="bg-card border-border/50">
          <CardContent className="report-card">
            <h2 className="report-section-title flex items-center gap-2">
              <Activity className="h-5 w-5 text-amber-400" strokeWidth={1.5} />
              Neural Friction Drivers
            </h2>
            <p className="report-subheading">Key areas of cognitive stress impacting your mental operating system</p>
            <div className="space-y-3">
              {interpretation?.neural_stress_drivers?.map((driver, i) => {
                const severityColors = { high: '#EF4444', moderate: '#F59E0B', low: '#10B981' };
                return (
                  <div key={i} className="p-5 rounded-lg bg-secondary/20 border border-border/40">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="font-semibold text-base">{driver.name}</h4>
                        <p className="text-sm leading-relaxed mt-1.5" style={{ color: 'hsl(var(--muted-foreground))' }}>{driver.description}</p>
                      </div>
                      <span className="text-xs font-medium px-3 py-1 rounded-full shrink-0" style={{
                        backgroundColor: `${severityColors[driver.severity]}15`,
                        color: severityColors[driver.severity],
                        border: `1px solid ${severityColors[driver.severity]}30`
                      }}>
                        {driver.severity}
                      </span>
                    </div>
                  </div>
                );
              }) || report.top_bottlenecks?.map((b, i) => (
                <div key={i} className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <p className="text-sm">{b}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* 4. Domain Score Breakdown */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
        <Card className="bg-card border-border/50" id="domain-score-breakdown">
          <CardContent className="report-card">
            <h2 className="report-section-title">Domain Score Breakdown</h2>
            <p className="report-subheading">Individual performance across all 7 cognitive domains</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 xl:grid-cols-4">
              {report.domain_scores?.map((ds, i) => {
                const level = getScoreLevel(ds.dysfunction_score);
                const health = 100 - ds.dysfunction_score;
                return (
                  <div key={i} className="p-4 rounded-lg bg-secondary/20 border border-border/40">
                    <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">
                      {DOMAIN_NAMES[ds.domain]}
                    </p>
                    <div className="flex items-end gap-2">
                      <span className="text-3xl font-bold font-mono" style={{ color: level.color }}>
                        {health.toFixed(0)}
                      </span>
                      <span className="text-xs mb-1" style={{ color: level.color }}>{level.label}</span>
                    </div>
                    <div className="mt-2 h-1.5 rounded-full bg-secondary overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{
                        width: `${health}%`, backgroundColor: level.color
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* 5. Mind Architecture Wheel */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
        <Card className="bg-card border-border/50" id="mind-architecture-wheel">
          <CardContent className="report-card">
            <h2 className="report-section-title">Mind Architecture Wheel</h2>
            <p className="report-subheading">Radar view of your cognitive health across all domains</p>
            <MindWheel domainScores={report.domain_scores} size="large" />
          </CardContent>
        </Card>
      </motion.div>

      {/* 6. Neural Friction Map */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
        <Card className="bg-card border-border/50" id="neural-friction-map">
          <CardContent className="report-card">
            <h2 className="report-section-title">Neural Friction Map</h2>
            <p className="report-subheading">Friction intensity across each cognitive domain</p>
            {report.domain_scores && (
              <NeuralFrictionMap domainScores={report.domain_scores} />
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* 7. Belief Architecture Map */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
        <Card className="bg-card border-border/50" id="belief-architecture-map">
          <CardContent className="report-card">
            <h2 className="report-section-title">Belief Architecture Map</h2>
            <p className="report-subheading">How belief patterns cascade through your cognitive system</p>
            <div className="flex justify-center">
              <BeliefArchitectureMap beliefPattern={pattern} domainScores={report.domain_scores} />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* 8. Upgrade Path Recommendation */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}>
        <Card className="bg-card border-primary/20" id="upgrade-path-recommendation">
          <CardContent className="report-card">
            <h2 className="report-section-title flex items-center gap-2">
              <Zap className="h-5 w-5 text-primary" strokeWidth={1.5} />
              Upgrade Path Recommendation
            </h2>
            <p className="report-subheading">Prioritized optimization modules based on your diagnostic profile</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {interpretation?.upgrade_path_modules?.map((module, i) => (
                <div key={i} className="p-5 rounded-lg bg-primary/5 border border-primary/20">
                  <span className="report-label text-primary">Module {module.priority}</span>
                  <h4 className="font-bold text-primary mt-2 text-base">{module.name}</h4>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{module.description}</p>
                  <p className="text-xs font-mono text-muted-foreground mt-3">Duration: {module.estimated_duration}</p>
                </div>
              )) || (
                <>
                  {['Belief Reprogramming', 'Cognitive Energy Reset', 'Executive Optimization'].map((name, i) => (
                    <div key={i} className="p-5 rounded-lg bg-primary/5 border border-primary/20">
                      <span className="report-label text-primary">Module {i + 1}</span>
                      <h4 className="font-bold text-primary mt-2">{name}</h4>
                    </div>
                  ))}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* 9. 30-Day Cognitive Focus & Strengths */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
          <Card className="bg-card border-border/50 h-full">
            <CardContent className="report-card">
              <h2 className="report-section-title flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" strokeWidth={1.5} />
                30-Day Cognitive Focus
              </h2>
              <p className="report-subheading">Your immediate optimization priority</p>
              <p className="report-body">{report.thirty_day_focus}</p>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}>
          <Card className="bg-card border-border/50 h-full">
            <CardContent className="report-card">
              <h2 className="report-section-title flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" strokeWidth={1.5} />
                Long-Term Development
              </h2>
              <p className="report-subheading">Sustained cognitive evolution path</p>
              <p className="report-body">{report.long_term_path}</p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Strengths & Friction Points */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" id="behavioral-insights">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
          <Card className="bg-card border-border/50 h-full">
            <CardContent className="report-card">
              <h2 className="report-section-title flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-emerald-400" strokeWidth={1.5} />
                Cognitive Strengths
              </h2>
              <div className="space-y-2.5 mt-4">
                {report.top_strengths?.map((s, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-emerald-500/8 border border-emerald-500/15">
                    <span className="text-xs font-mono font-bold text-emerald-400">0{i + 1}</span>
                    <span className="text-sm">{s}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }}>
          <Card className="bg-card border-border/50 h-full">
            <CardContent className="report-card">
              <h2 className="report-section-title flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-400" strokeWidth={1.5} />
                Top Friction Points
              </h2>
              <div className="space-y-2.5 mt-4">
                {report.top_bottlenecks?.map((b, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-amber-500/8 border border-amber-500/15">
                    <span className="text-xs font-mono font-bold text-amber-400">0{i + 1}</span>
                    <span className="text-sm">{b}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Action CTA */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}>
        <Card className="bg-card border-border/50">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-base">Ready to begin your cognitive evolution?</p>
              <div className="flex gap-3">
                <Link to="/mind-map">
                  <Button variant="outline" data-testid="view-mind-map">View Mind Map</Button>
                </Link>
                <Link to="/upgrade-path">
                  <Button className="glow-primary" data-testid="view-pathways">
                    Start Upgrade Path
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
